import currencyService from '../../../services/currencyService';
import filterService from '../../../services/filterService';

import {
    setCurrencyLoading,
    setCurrencyError,
    setCurrencyList,
    setCurrencyCurrent,
    setCurrencyPagination,
    setCurrencySort,
    setCurrencyFilters,
    setCurrencyDefaultFilter,
    setCurrencySettings,
    clearCurrencyCurrent,
    clearCurrencyError,
    setCurrencyFilterLoading,
    changeCurrencyFilterCondition,
    addCurrencyFilterCondition,
    updateCurrencyFilterConditionRule,
    updateCurrencyFilterConditionValue,
    removeCurrencyFilterCondition,
    setCurrencySelected,
    resetCurrencyFiltersAndPagination
} from './currencySlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().currency.isSettingsInitialized) {
            dispatch(setCurrencyLoading(true));
            const response = await currencyService.getSettings();

            // Set the settings
            dispatch(setCurrencySettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setCurrencyDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setCurrencyError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setCurrencyLoading(false));
    }
};

export const fetchCurrencies = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().currency;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setCurrencyLoading(true));
        const response = await currencyService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setCurrencyList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchCurrencies:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setCurrencyError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setCurrencyLoading(false));
    }
};

export const fetchCurrency = (id) => async (dispatch) => {
    try {
        dispatch(setCurrencyLoading(true));
        const response = await currencyService.getOne(id);
        dispatch(setCurrencyCurrent(response.data));
    } catch (error) {
        dispatch(setCurrencyError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setCurrencyLoading(false));
    }
};

export const createCurrency = (data) => async (dispatch) => {
    try {
        const response = await currencyService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateCurrency = (id, data) => async (dispatch) => {
    try {
        const response = await currencyService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteCurrency = (id) => async (dispatch) => {
    try {
        dispatch(setCurrencyLoading(true));
        const response = await currencyService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setCurrencyError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setCurrencyLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setCurrencyPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setCurrencySort(sort));
};

export const searchCurrencies = (query) => (dispatch, getState) => {
    const currentFilters = getState().currency.filters;
    dispatch(setCurrencyFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setCurrencyDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeCurrencyFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addCurrencyFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateCurrencyFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateCurrencyFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeCurrencyFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setCurrencyFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().currency.settings;
            dispatch(setCurrencySettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setCurrencyFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setCurrencyFilterLoading(false));
        dispatch(setCurrencyError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setCurrencySelected(selectedRows));
    };
};

export const clearCurrentCurrency = () => {
    return (dispatch) => {
        dispatch(clearCurrencyCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetCurrencyFiltersAndPagination());
};